<script lang="ts">
  import { onMount } from "svelte";
  import {
    listDiagLinks,
    createDiagLink,
    regenerateDiagLink,
    revokeDiagLink,
    listDevicesLogs,
    listUsers,
    ApiError,
  } from "../lib/api";
  import { push } from "svelte-spa-router";
  import type {
    DiagLinkInfo,
    DeviceLogInfo,
    AdminUser,
  } from "@diy-avatar/shared/admin";

  const TTL_OPTIONS = [1, 6, 12, 24, 48, 168];

  let links: DiagLinkInfo[] = [];
  let linksLoading = false;
  let linksError: string | null = null;

  let devices: DeviceLogInfo[] = [];
  let devicesError: string | null = null;

  let users: AdminUser[] = [];
  let usersError: string | null = null;

  // create form state
  let scopeKind: "all" | "device" | "user" = "all";
  let formScope = ""; // "" => all devices
  let formUserId: number | null = null;
  let formUserFilter = "";
  let formTtl = 12;
  let creating = false;
  let createError: string | null = null;
  let newLink: DiagLinkInfo | null = null;

  // lookup of user id -> user for display in the scope column
  $: usersById = new Map<number, AdminUser>(users.map((u) => [u.id, u]));

  function firstRole(u: AdminUser): string | null {
    return u.roles.length > 0 ? u.roles[0].role : null;
  }

  function isStudent(u: AdminUser): boolean {
    return u.roles.some((r) => r.role === "student");
  }

  // filtered + sorted (students first) user list for the picker
  $: filteredUsers = users
    .filter((u) => {
      const q = formUserFilter.trim().toLowerCase();
      if (q === "") return true;
      const name = (u.display_name ?? "").toLowerCase();
      return u.email.toLowerCase().includes(q) || name.includes(q);
    })
    .slice()
    .sort((a, b) => {
      const sa = isStudent(a) ? 0 : 1;
      const sb = isStudent(b) ? 0 : 1;
      if (sa !== sb) return sa - sb;
      const na = a.display_name || a.email;
      const nb = b.display_name || b.email;
      return na.localeCompare(nb);
    });

  // transient "Copied!" feedback, keyed by token (or "__new" for the banner)
  let copied: Record<string, boolean> = {};

  function handle401(err: unknown): boolean {
    if (err instanceof ApiError && err.status === 401) {
      push("/");
      return true;
    }
    return false;
  }

  function fmtDate(ms: number | null): string {
    if (ms === null) return "—";
    return new Date(ms).toISOString().slice(0, 19).replace("T", " ");
  }

  function truncate(s: string, n = 56): string {
    return s.length > n ? `${s.slice(0, n)}…` : s;
  }

  function scopeLabel(link: DiagLinkInfo): string {
    if (link.device_label) return link.device_label;
    const uid = link.scope_user_id;
    if (uid != null) {
      const u = usersById.get(uid);
      return u ? u.email : `User #${uid}`;
    }
    return "All";
  }

  function buildDiagPrompt(url: string): string {
    return `# Device Diagnostic API

You are diagnosing a device fleet through a read-only diagnostic API. Use the link below as the base URL. The access token is already embedded in the URL path. Every request is a plain GET. The API is read-only and the link is time-limited.

Base URL:
${url}

## Step 1 — Always start with the manifest
GET ${url}
Returns: scope (all, a single device, or a single user), expiry, the list of devices, available log levels, and the endpoint list. Read this first to learn the scope before querying anything else.

## Endpoints (append the path to the base URL)
- GET ${url}/logs?level=&since=&until=&device=&target_type=&target_id=&session_id=&limit=
  Filtered log entries. level = comma-separated list (e.g. error,warn) or "all" for no filter; default is error,warn. since/until = Unix epoch MILLISECONDS (integers). target_type/target_id and session_id narrow to a specific subject/session. limit <= 2000 (default 200). Results are newest-last; to page further back, take the returned next_cursor and pass it back as until.
- GET ${url}/search?q=&since=&until=&level=&device=&target_type=&target_id=&limit=
  Full-text search over log messages, newest first. q is the search query; since/until = Unix epoch MILLISECONDS; the other filters match /logs.
- GET ${url}/summary?since=&until=&device=
  Error/warn counts per device and for the server, plus the top recurring messages. Best first triage step.
- GET ${url}/devices
  Live device status (latest heartbeat + hardware enumeration).
- GET ${url}/audit?since=&limit=
  Audit-log events, newest first. NOTE: here \`since\` is an ID cursor (not a timestamp); page older events by passing the returned next_cursor.

## Scope
A link may be scoped to all data, a single device, or a single user (e.g. a student). Queries that fall outside the link's scope return 403. Always use the scope, devices, and user reported by the manifest.

## Timestamps
All times are Unix milliseconds. For a 1-hour window use since = (now_ms - 3600000). For 24h use 86400000.

## Suggested workflow
1. GET the manifest to learn scope, devices, and user.
2. GET /summary over a recent window to find the noisy device/level.
3. Drill in with /search (by keyword) or /logs (by device/level/target).
4. Cross-check /devices for liveness and /audit for recent admin actions.`;
  }

  async function copy(text: string, key: string) {
    try {
      await navigator.clipboard.writeText(text);
      copied = { ...copied, [key]: true };
      setTimeout(() => {
        copied = { ...copied, [key]: false };
      }, 1500);
    } catch {
      /* clipboard may be unavailable; ignore */
    }
  }

  async function loadLinks() {
    linksLoading = true;
    linksError = null;
    try {
      const res = await listDiagLinks();
      links = res.links;
    } catch (err) {
      if (handle401(err)) return;
      linksError =
        err instanceof Error ? err.message : "Could not load diagnostic links.";
    } finally {
      linksLoading = false;
    }
  }

  async function loadDevices() {
    devicesError = null;
    try {
      const res = await listDevicesLogs();
      devices = res.devices;
    } catch (err) {
      if (handle401(err)) return;
      devicesError =
        err instanceof Error ? err.message : "Could not load devices.";
    }
  }

  async function loadUsers() {
    usersError = null;
    try {
      const res = await listUsers();
      users = res.users;
    } catch (err) {
      if (handle401(err)) return;
      usersError =
        err instanceof Error ? err.message : "Could not load users.";
    }
  }

  async function create() {
    createError = null;
    let req: import("@diy-avatar/shared/admin").CreateDiagLinkRequest;
    if (scopeKind === "device") {
      if (formScope === "") {
        createError = "Select a device for a device-scoped link.";
        return;
      }
      req = { device_label: formScope, ttl_hours: formTtl };
    } else if (scopeKind === "user") {
      if (formUserId == null) {
        createError = "Select a user for a user-scoped link.";
        return;
      }
      req = { scope_user_id: formUserId, ttl_hours: formTtl };
    } else {
      req = { ttl_hours: formTtl };
    }
    creating = true;
    try {
      const res = await createDiagLink(req);
      newLink = res.link;
      links = [res.link, ...links];
    } catch (err) {
      if (handle401(err)) return;
      createError =
        err instanceof Error ? err.message : "Could not create link.";
    } finally {
      creating = false;
    }
  }

  async function regenerate(link: DiagLinkInfo) {
    if (
      !window.confirm(
        "Regenerate this link? The old URL stops working immediately.",
      )
    )
      return;
    try {
      await regenerateDiagLink(link.token);
      await loadLinks();
    } catch (err) {
      if (handle401(err)) return;
      linksError =
        err instanceof Error ? err.message : "Could not regenerate link.";
    }
  }

  async function revoke(link: DiagLinkInfo) {
    if (!window.confirm("Revoke this link? It can no longer be used.")) return;
    try {
      await revokeDiagLink(link.token);
      links = links.filter((l) => l.token !== link.token);
      if (newLink && newLink.token === link.token) newLink = null;
    } catch (err) {
      if (handle401(err)) return;
      linksError =
        err instanceof Error ? err.message : "Could not revoke link.";
    }
  }

  onMount(() => {
    void loadLinks();
    void loadDevices();
    void loadUsers();
  });
</script>

<section>
  <h1>Diagnostic links</h1>

  <p class="note" data-testid="diag-note">
    Share this URL with the AI diagnostic agent. It is read-only, time-limited,
    and can be revoked anytime.
  </p>

  <div class="panel" data-testid="diag-create-section">
    <h2>Create link</h2>
    <div class="row">
      <label>
        Scope
        <select bind:value={scopeKind} data-testid="diag-scope-kind">
          <option value="all">All devices</option>
          <option value="device">Single device</option>
          <option value="user">Single user</option>
        </select>
      </label>
      {#if scopeKind === "device"}
        <label>
          Device
          <select bind:value={formScope} data-testid="diag-scope">
            <option value="">Select a device…</option>
            {#each devices as d (d.label)}
              <option value={d.label}>{d.label}</option>
            {/each}
          </select>
        </label>
      {/if}
      {#if scopeKind === "user"}
        <label>
          Filter users
          <input
            type="text"
            placeholder="email or name…"
            bind:value={formUserFilter}
            data-testid="diag-user-filter"
          />
        </label>
        <label>
          User
          <select bind:value={formUserId} data-testid="diag-user-select">
            <option value={null}>Select a user…</option>
            {#each filteredUsers as u (u.id)}
              <option value={u.id}>
                {u.display_name || u.email}{firstRole(u)
                  ? ` (${firstRole(u)})`
                  : ""}
              </option>
            {/each}
          </select>
        </label>
      {/if}
      <label>
        TTL (hours)
        <select bind:value={formTtl} data-testid="diag-ttl">
          {#each TTL_OPTIONS as h}
            <option value={h}>{h}</option>
          {/each}
        </select>
      </label>
      <button on:click={create} disabled={creating} data-testid="diag-create-button">
        {creating ? "Creating…" : "Create link"}
      </button>
    </div>
    {#if devicesError}
      <p class="error" data-testid="diag-devices-error">{devicesError}</p>
    {/if}
    {#if usersError}
      <p class="error" data-testid="diag-users-error">{usersError}</p>
    {/if}
    {#if createError}
      <p class="error" data-testid="diag-create-error">{createError}</p>
    {/if}
    {#if newLink}
      {@const newUrl = newLink.url}
      <div class="banner" data-testid="diag-new-link">
        <span class="url">{newUrl}</span>
        <button on:click={() => copy(buildDiagPrompt(newUrl), "__new")} data-testid="diag-new-copy">
          {copied["__new"] ? "Copied!" : "Copy prompt"}
        </button>
        <button
          class="icon-btn"
          title="Copy link"
          aria-label="Copy link"
          on:click={() => copy(newUrl, "__new:link")}
          data-testid="diag-new-copy-link"
        >
          {#if copied["__new:link"]}
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
          {:else}
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
          {/if}
        </button>
      </div>
    {/if}
  </div>

  <div class="panel" data-testid="diag-links-section">
    <h2>Active links</h2>
    {#if linksError}
      <p class="error" data-testid="diag-links-error">{linksError}</p>
    {/if}
    <table data-testid="diag-links-table">
      <thead>
        <tr>
          <th>id</th>
          <th>url</th>
          <th>scope</th>
          <th>created</th>
          <th>expires</th>
          <th>last used</th>
          <th>actions</th>
        </tr>
      </thead>
      <tbody>
        {#each links as l (l.token)}
          <tr data-testid="diag-link-row-{l.token}">
            <td><span class="url" title={l.token}>{truncate(l.token)}</span></td>
            <td>
              <span class="url" title={l.url}>{truncate(l.url)}</span>
            </td>
            <td>{scopeLabel(l)}</td>
            <td>{fmtDate(l.created_at)}</td>
            <td>{fmtDate(l.expires_at)}</td>
            <td>{fmtDate(l.last_used_at)}</td>
            <td class="actions">
              <button on:click={() => copy(buildDiagPrompt(l.url), l.token)} data-testid="diag-copy">
                {copied[l.token] ? "Copied!" : "Copy prompt"}
              </button>
              <button
                class="icon-btn"
                title="Copy link"
                aria-label="Copy link"
                on:click={() => copy(l.url, `${l.token}:link`)}
                data-testid="diag-copy-link"
              >
                {#if copied[`${l.token}:link`]}
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
                {:else}
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
                {/if}
              </button>
              <button on:click={() => regenerate(l)} data-testid="diag-regenerate">
                Regenerate
              </button>
              <button on:click={() => revoke(l)} data-testid="diag-revoke">
                Revoke
              </button>
            </td>
          </tr>
        {/each}
        {#if links.length === 0 && !linksLoading}
          <tr>
            <td colspan="7" class="empty" data-testid="diag-links-empty">
              No active links.
            </td>
          </tr>
        {/if}
      </tbody>
    </table>
  </div>
</section>

<style>
  h1 {
    margin: 0 0 1rem;
  }
  h2 {
    margin: 0 0 0.5rem;
    font-size: 1rem;
  }
  .note {
    color: #9ca3af;
    font-size: 0.85rem;
    margin: 0 0 1rem;
  }
  .panel {
    margin-bottom: 1rem;
    padding: 0.75rem 1rem;
    background: #111827;
    border: 1px solid #1f2937;
    border-radius: 8px;
  }
  .row {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
    align-items: flex-end;
  }
  label {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    font-size: 0.8rem;
    color: #9ca3af;
  }
  select,
  input {
    padding: 0.4rem 0.5rem;
    background: #0f172a;
    color: #f3f4f6;
    border: 1px solid #374151;
    border-radius: 4px;
  }
  button {
    padding: 0.4rem 0.7rem;
    background: #1f2937;
    color: #f3f4f6;
    border: 1px solid #374151;
    border-radius: 4px;
  }
  button:disabled {
    opacity: 0.6;
  }
  .banner {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.75rem;
    padding: 0.5rem 0.7rem;
    background: #0b1220;
    border: 1px solid #1f2937;
    border-radius: 6px;
  }
  .url {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.8rem;
    word-break: break-all;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    background: #111827;
  }
  th,
  td {
    padding: 0.4rem 0.6rem;
    text-align: left;
    border-bottom: 1px solid #1f2937;
    font-size: 0.85rem;
  }
  th {
    background: #0f172a;
    color: #9ca3af;
  }
  .actions {
    display: flex;
    gap: 0.3rem;
    flex-wrap: wrap;
    align-items: center;
  }
  .icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.35rem;
    line-height: 0;
    color: #f3f4f6;
  }
  .icon-btn svg {
    display: block;
  }
  .empty {
    color: #6b7280;
    text-align: center;
  }
  .error {
    color: #fca5a5;
  }
</style>
